import * as Yup from "yup";
import { tWithKeyAttr as t } from "../i18nHelper";
import { TContactPerson } from "../services/types/user.types";
import { isDeviceContact } from "./deviceConfiguration";

export const internationalPhoneRegExp = /^([\+]?[1-9][\d]{0,15}|05\d{8})$/;

// Stricter email validation that requires a TLD (at least one dot in domain)
export const emailRegExp = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export const loginEmailValidationSchema = Yup.object().shape({
  email: Yup.string()
    .required("REQUIRED_FIELDS")
    .matches(emailRegExp, "INVALID_EMAIL"),
  password: Yup.string().required("REQUIRED_FIELDS").min(6, "PASSWORD_MIN"),
});

export const loginSMSValidationSchema = Yup.object().shape({
  phone: Yup.string()
    .required("PHONE_REQUIRED")
    .matches(internationalPhoneRegExp, "PHONE_INVALID"),
});

const contactSchema = Yup.object({
  firstName: Yup.string().required("REQUIRED_FIELDS").min(2, "FIRSTNAME_MIN"),
  phone: Yup.string().matches(internationalPhoneRegExp, "PHONE_INVALID"),
  email: Yup.string()
    .required("REQUIRED_FIELDS")
    .matches(emailRegExp, "INVALID_EMAIL")
});

const deviceContactSchema = Yup.object({
  firstName: Yup.string().required("REQUIRED_FIELDS").min(2, "FIRSTNAME_MIN"),
  deviceConfig: Yup.object({
    capabilities: Yup.array()
      .of(Yup.string().oneOf(["nf", "loreta", "scan", "tdcs", "tacs"]))
      .min(1, "DEVICE_CAPABILITIES_REQUIRED"),
  }).required("DEVICE_CONFIG_REQUIRED"),
});

export const addNewUserValidationSchema = Yup.object().shape({
  firstName: Yup.string().required("REQUIRED_FIELDS").min(2, "FIRSTNAME_MIN"),
  lastName: Yup.string().min(2, "LASTNAME_MIN"),
  phone: Yup.string()
    .matches(internationalPhoneRegExp, { message: "PHONE_INVALID", excludeEmptyString: true })
    // Patients (roleId 1) may be created with a phone instead of an email
    .when(["roleId", "email"], {
      is: (roleId: number, email: string) => roleId === 1 && !email,
      then: (schema) => schema.required("PHONE_OR_EMAIL_REQUIRED"),
    }),
  email: Yup.string()
    .matches(emailRegExp, { message: "INVALID_EMAIL", excludeEmptyString: true })
    // Mandatory for staff; for patients required only until a phone is entered,
    // so both fields flag red while neither contact method is filled
    .when(["roleId", "phone"], {
      is: (roleId: number, phone: string) => roleId !== 1 || !phone,
      then: (schema) => schema.required("PHONE_OR_EMAIL_REQUIRED"),
    }),
  birth: Yup.date()
    .nullable()
    .min(new Date(1920, 0, 1), "BIRTH_INVALID")
    .max(new Date(), "BIRTH_INVALID"),
  passport: Yup.string().nullable().min(9, "PASSPORT_LENGTH").max(9, "PASSPORT_LENGTH"),
  password: Yup.lazy((_, { parent }) =>
    parent.roleId !== 1 && !parent.guid
      ? Yup.string().required("PASSWORD_REQUIRED")
      : Yup.string().notRequired()
  ),
  contacts: Yup.array()
    .of(
      Yup.lazy((value: TContactPerson) =>
        isDeviceContact(value ?? {}) ? deviceContactSchema : contactSchema
      )
    )
    .nullable()
    .test(
      "validate-only-if-not-empty",
      "Invalid contacts",
      (value) => {
        // skip validation if no contacts at all
        if (!value || value.length === 0) return true;
        // at least one contact has some data — allow Yup to handle inner validation
        return true;
      }
    ),
  // email <-> phone reference each other; declare the pair so Yup skips cyclic ordering
}, [["email", "phone"]]);

export const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .required("PASSWORD_REQUIRED")
    .min(6, "PASSWORD_MIN")
    .matches(/[A-Za-zא-ת]/, "PASSWORD_LETTER")
    .matches(/\d/, "PASSWORD_NUMBER")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "PASSWORD_SPECIAL"),
  repeatPassword: Yup.string()
    .oneOf([Yup.ref("password")], "PASSWORDS_MATCH")
    .required("REPEAT_PASSWORD_REQUIRED"),
});

// Format: international phone or Israeli mobile
const phoneRegExp = /^([\+]?[1-9][\d]{0,15}|05\d{8})$/;

export const updateUserValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("REQUIRED_FIELDS")
    .min(2, "FIRSTNAME_MIN"),
  lastName: Yup.string()
    .required("REQUIRED_FIELDS")
    .min(2, "LASTNAME_MIN"),
  email: Yup.string()
    .required("REQUIRED_FIELDS")
    .matches(emailRegExp, "INVALID_EMAIL"),
  phone: Yup.string()
    .required("REQUIRED_FIELDS")
    .matches(phoneRegExp, "PHONE_INVALID"),
  degree: Yup.string(),
});

export const treatmentReportValidationSchema = Yup.object().shape({
  referralReasons: Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  lifeHistory: Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  mentalStatusAtIntake: Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  functioningAtIntake: Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  isDangerObserved: Yup.mixed()
    .oneOf([true, false], t("VALIDATION_SELECT_YES_OR_NO"))
    .required("VALIDATION_REQUIRED_FIELD"),

  isAddictionObserved: Yup.mixed()
    .oneOf([true, false], t("VALIDATION_SELECT_YES_OR_NO"))
    .required("VALIDATION_REQUIRED_FIELD"),

  preliminaryDiagnosisICD10_DSM5: Yup.string().required(
    "VALIDATION_REQUIRED_FIELD"
  ),

  treatmentPlanAndGoals_AsDeterminedAtIntake_TreatmentFrequency_TherapeuticGoals_OtherNotes:
    Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  treatmentCourseDescription_NumberOfSessionsThemesEngagedResponse:
    Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  currentMentalStatusAppearanceBehaviorCooperationMoodAffectPerceptionThinkingProcessAndPace:
    Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  currentFunctioning: Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  therapeuticGoalsAchieved: Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  currentWorkingDiagnosisAccordingTo_ICD10_DSM5: Yup.string().required(
    "VALIDATION_REQUIRED_FIELD"
  ),

  therapistSignature: Yup.string().required("VALIDATION_REQUIRED_FIELD"),

  isDigitallySigned: Yup.boolean().oneOf(
    [true],
    "VALIDATION_MUST_BE_DIGITALLY_SIGNED"
  ),
});

export const newCompanyValidationSchema: Record<
  number,
  Yup.ObjectSchema<any>
> = {
  1: Yup.object({
    name: Yup.string().required("COMPANY_NAME_REQUIRED"),
    nameEn: Yup.string()
      .required("COMPANY_NAME_EN_REQUIRED")
      .matches(
        /^[A-Za-z0-9_-]+$/,
        "COMPANY_NAME_EN_LETTERS_ONLY"
      ),
    email: Yup.string()
      .matches(emailRegExp, "INVALID_EMAIL")
      .required("EMAIL_REQUIRED"),
    phone: Yup.string().required("PHONE_REQUIRED").matches(internationalPhoneRegExp, "PHONE_INVALID"),
  }),
  2: Yup.object({
    contactFullName: Yup.string().required("CONTACT_PERSON_NAME_REQUIRED"),
    positionTitle: Yup.string().required("POSITION_REQUIRED"),
  }),
  3: Yup.object({
    companySizeId: Yup.string().required("SELECT_COMPANY_SIZE"),
    sectorId: Yup.string().required("SELECT_SECTOR"),
    prefferedLanguageId: Yup.string().required("SELECT_PREFERRED_LANGUAGE"),
  }),
  4: Yup.object({
    selectedPlanId: Yup.string().required("SELECT_PLAN_REQUIRED"),
  }),
  5: Yup.object({}), // No validation required for user management step
  6: Yup.object({}), // No validation required for success step
  7: Yup.object({}), // No validation required for additional settings step
  8: Yup.object({}), // No validation required for welcome step
};
